import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react"; 
import { useTranslation } from "react-i18next";
import { Toast } from "../components";

interface ToastData {
  description: string;
  type: "success" | "error";
  message: string;
  duration: number;
}

interface ToastContextProps {
  showToast: (
    description: string,
    type: "success" | "error",
    message?: string,
    duration?: number,
  ) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<ToastData | null>(null);
  const { t } = useTranslation();

  const showToast = useCallback(
    (
      description: string,
      type: "success" | "error",
      message?: string,
      duration = 2000,
    ) => {
      const defaultMessage =
        type === "success" ? t("success_toaster_title") : t("error");

      setToast({
        description,
        type,
        message: message || defaultMessage, 
        duration,
      });
    },
    [t],
  );

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast && (
        <Toast
          description={toast.description}
          type={toast.type}
          duration={toast.duration}
          message={toast.message}
          onClose={hideToast}
        />
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextProps => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
